///
/// EarthBrowser
///
/// Created by Matt Giger on 2/3/2025
/// Copyright (c) 2025 Geodata Labs
///

import { StovokorContext } from "@stovokor/StovokorContext"
import { color } from "@stovokor/math/color"
import { mat3, matrix } from "@stovokor/math/matrix"
import { range } from "@stovokor/math/range"
import { vec2, vec3, vec4 } from "@stovokor/math/vector"
import { type ActionContainer, type ShaderAction } from "@stovokor/shader/ShaderActions"
import { Texture } from "@stovokor/shader/Texture"
import { TextureCube } from "@stovokor/shader/TextureCube"

export type uniformType = boolean | number | vec2 | vec3 | vec4 | range | color | matrix | mat3 | Texture | TextureCube

// Upload path, resolved once from the active uniform's GL type so per-frame
// uploads are a single switch instead of a chain of instanceof checks
enum UniformKind {
	Unsupported,
	Int,
	Float,
	Vec2,
	Vec3,
	Vec4,
	Mat3,
	Mat4,
	Sampler2D,
	SamplerCube
}

function kindOf(type: GLenum): UniformKind {
	switch(type) {
		case WebGL2RenderingContext.BOOL:
		case WebGL2RenderingContext.INT:
			return UniformKind.Int
		case WebGL2RenderingContext.FLOAT:
			return UniformKind.Float
		case WebGL2RenderingContext.FLOAT_VEC2:
			return UniformKind.Vec2
		case WebGL2RenderingContext.FLOAT_VEC3:
			return UniformKind.Vec3
		case WebGL2RenderingContext.FLOAT_VEC4:
			return UniformKind.Vec4
		case WebGL2RenderingContext.FLOAT_MAT3:
			return UniformKind.Mat3
		case WebGL2RenderingContext.FLOAT_MAT4:
			return UniformKind.Mat4
		case WebGL2RenderingContext.SAMPLER_2D:
		case WebGL2RenderingContext.INT_SAMPLER_2D:
		case WebGL2RenderingContext.UNSIGNED_INT_SAMPLER_2D:
			return UniformKind.Sampler2D
		case WebGL2RenderingContext.SAMPLER_CUBE:
			return UniformKind.SamplerCube
		default:
			return UniformKind.Unsupported
	}
}

export class UniformBinding {
	public info: WebGLActiveInfo
	public location: WebGLUniformLocation
	public textureUnit: number = -1

	private readonly kind: UniformKind
	// Sampler uniforms hold their unit index for the life of the program, so
	// the uniform1i only needs to go out once per link
	private unitAssigned: boolean = false
	private warned: boolean = false

	// Last value uploaded to this program's uniform. Uniform state lives in
	// the program object, so when many renderers share a program (every
	// earth tile) the values that are the same for all of them (light
	// direction, camera position, time...) only need one upload per frame.
	// Components are copied rather than the object referenced, so a value
	// mutated in place still compares as changed.
	private lastScalar: number = Number.NaN
	private readonly lastComponents = new Float32Array(16)
	private lastValid: boolean = false

	private context: StovokorContext
	constructor(context: StovokorContext, info: WebGLActiveInfo, location: WebGLUniformLocation) {
		this.context = context
		this.info = info
		this.location = location
		this.kind = kindOf(info.type)
		if(this.kind === UniformKind.Unsupported) {
			context.logger.error(`Unsupported uniform type: ${info.type} (${info.name})`)
		}
	}

	public get isTexture(): boolean {
		return this.kind === UniformKind.Sampler2D || this.kind === UniformKind.SamplerCube
	}

	/** Upload a value to this uniform on the currently bound program, skipping it if unchanged */
	public upload(value: uniformType): void {
		const gl = this.context.gl
		switch(this.kind) {
			case UniformKind.Int: {
				const n = typeof value === 'number' ? value : typeof value === 'boolean' ? (value ? 1 : 0) : undefined
				if(n === undefined) {
					this.mismatch(value)
				} else if(n !== this.lastScalar) {
					gl.uniform1i(this.location, n)
					this.lastScalar = n
				}
				return
			}
			case UniformKind.Float: {
				const n = typeof value === 'number' ? value : typeof value === 'boolean' ? (value ? 1 : 0) : undefined
				if(n === undefined) {
					this.mismatch(value)
				} else if(n !== this.lastScalar) {
					gl.uniform1f(this.location, n)
					this.lastScalar = n
				}
				return
			}
			case UniformKind.Vec2:
				if(value instanceof vec2) {
					if(this.changed2(value.x, value.y)) gl.uniform2f(this.location, value.x, value.y)
				} else if(value instanceof range) {
					if(this.changed2(value.min, value.max)) gl.uniform2f(this.location, value.min, value.max)
				} else {
					this.mismatch(value)
				}
				return
			case UniformKind.Vec3:
				if(value instanceof vec3) {
					if(this.changed3(value.x, value.y, value.z)) gl.uniform3f(this.location, value.x, value.y, value.z)
				} else {
					this.mismatch(value)
				}
				return
			case UniformKind.Vec4:
				if(value instanceof vec4) {
					if(this.changed4(value.x, value.y, value.z, value.w)) gl.uniform4f(this.location, value.x, value.y, value.z, value.w)
				} else if(value instanceof color) {
					if(this.changed4(value.r, value.g, value.b, value.a)) gl.uniform4f(this.location, value.r, value.g, value.b, value.a)
				} else {
					this.mismatch(value)
				}
				return
			case UniformKind.Mat3:
				if(value instanceof mat3) {
					const array = value.glArrayAccess()
					if(this.changedArray(array, 9)) gl.uniformMatrix3fv(this.location, false, array)
				} else {
					this.mismatch(value)
				}
				return
			case UniformKind.Mat4:
				if(value instanceof matrix) {
					const array = value.glArrayAccess()
					if(this.changedArray(array, 16)) gl.uniformMatrix4fv(this.location, false, array)
				} else {
					this.mismatch(value)
				}
				return
			case UniformKind.Sampler2D:
				if(value instanceof Texture) {
					// A texture whose storage isn't allocated yet samples the
					// blank texture rather than whatever the unit last held
					const id = value.textureId ?? this.context.blankTexture.textureId
					this.context.glState.bindTextureUnit(this.textureUnit, WebGL2RenderingContext.TEXTURE_2D, id)
					this.assignUnit()
				} else {
					this.mismatch(value)
				}
				return
			case UniformKind.SamplerCube:
				if(value instanceof TextureCube) {
					if(value.textureId) {
						this.context.glState.bindTextureUnit(this.textureUnit, WebGL2RenderingContext.TEXTURE_CUBE_MAP, value.textureId)
					}
					this.assignUnit()
				} else {
					this.mismatch(value)
				}
				return
			case UniformKind.Unsupported:
				return
		}
	}

	private assignUnit(): void {
		if(!this.unitAssigned) {
			this.context.gl.uniform1i(this.location, this.textureUnit)
			this.unitAssigned = true
		}
	}

	// Compare against the last uploaded components and record the new ones.
	// Values go through Float32Array so the comparison sees exactly what the
	// GPU received (uniform*f take floats)
	private changed2(x: number, y: number): boolean {
		const last = this.lastComponents
		if(this.lastValid && last[0] === Math.fround(x) && last[1] === Math.fround(y)) {
			return false
		}
		last[0] = x
		last[1] = y
		this.lastValid = true
		return true
	}

	private changed3(x: number, y: number, z: number): boolean {
		const last = this.lastComponents
		if(this.lastValid && last[0] === Math.fround(x) && last[1] === Math.fround(y) && last[2] === Math.fround(z)) {
			return false
		}
		last[0] = x
		last[1] = y
		last[2] = z
		this.lastValid = true
		return true
	}

	private changed4(x: number, y: number, z: number, w: number): boolean {
		const last = this.lastComponents
		if(this.lastValid && last[0] === Math.fround(x) && last[1] === Math.fround(y) && last[2] === Math.fround(z) && last[3] === Math.fround(w)) {
			return false
		}
		last[0] = x
		last[1] = y
		last[2] = z
		last[3] = w
		this.lastValid = true
		return true
	}

	private changedArray(array: Float32Array, length: number): boolean {
		const last = this.lastComponents
		if(this.lastValid) {
			let same = true
			for(let i = 0; i < length; i++) {
				if(last[i] !== array[i]) {
					same = false
					break
				}
			}
			if(same) {
				return false
			}
		}
		for(let i = 0; i < length; i++) {
			last[i] = array[i]
		}
		this.lastValid = true
		return true
	}

	private mismatch(value: unknown): void {
		if(!this.warned) {
			this.warned = true
			this.context.logger.warn(`[Uniform] ${this.info.name}: value does not match GL type ${this.info.type}:`, value)
		}
	}

	/** Action that uploads a fixed value; the value object is read at call time */
	public bindingFunction(value: uniformType): () => void {
		return () => this.upload(value)
	}
}

/**
 * A uniform whose value changes between frames. The renderer binds it to
 * the program's UniformBinding once; afterwards setting `value` is just a
 * field write and `action` is one stable closure (no per-frame allocation).
 */
export class UniformDynamic<T extends uniformType> implements ActionContainer {
	public readonly action: ShaderAction
	private uniform?: UniformBinding
	private _value?: T

	constructor(value?: T) {
		this._value = value
		this.action = () => {
			// Must not be a truthiness check: 0 and false are valid uniform
			// values. Skipping the upload leaves stale state from other
			// renderers sharing the same compiled program.
			if(this.uniform !== undefined && this._value !== undefined) {
				this.uniform.upload(this._value)
			}
		}
	}

	public setBinding(uniform: UniformBinding) {
		this.uniform = uniform
	}

	public get value(): T | undefined {
		return this._value
	}

	public set value(value: T) {
		this._value = value
	}
}
